import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, InfiniteScroll } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { DndProvider } from 'react-dnd-multi-backend'
import { HTML5toTouch } from 'rdndmb-html5-to-touch'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useTimeGridLayout } from 'modules/shift_management/hooks/gridTable/useTimeGridLayout'
import { useGridSelection } from 'modules/shift_management/hooks/gridTable/selection/useGridSelection'
import { useGridClipboard } from 'modules/shift_management/hooks/gridTable/useGridClipboard'
import { useClearSelectionOnOutsidePress } from 'modules/shift_management/hooks/gridTable/useClearSelectionOnOutsidePress'
import TimeGridHeader from './components/TimeGridHeader'
import type {
  TimeGridEntity,
  TimeGridEvent,
  TimeGridTableProps,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { GridVirtualization } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/GridVirtualization'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

const TimeGridTable = <TEntity extends TimeGridEntity, TEvent extends TimeGridEvent, TDragDrop>({
  entities,
  events,
  timeRange,
  granularity,
  rowHeaderLabel,
  rowHeaderWidth,
  minColumnWidth,
  rowMinHeight,
  paddingX,
  paddingY,
  cardLayout,
  showGridLines = true,
  stickyHeaders = true,
  enableHorizontalScroll = true,
  enableRowVirtualization = true,
  preloadedRowsOutsideViewport = 10,
  timeSlotFormatter,
  isCellSelectable,
  isCellDisabled,
  rowHeaderRenderer,
  eventRenderer,
  cardAppearance,
  secondaryLayerRenderer,
  rowActionsRenderer,
  dragDropHandlers,
  dragAndDropAdapter,
  clipboardHandlers,
  onDeleteShortcut,
  onSelectionChange,
  onAddShiftClick,
  ghostCells,
  ghostAnchorCell,
  onGhostCardRef,
  children,
  keyboardScrollMargin,
}: TimeGridTableProps<TEntity, TEvent, TDragDrop>) => {
  const [keyboardEndpointRowIndex, setKeyboardEndpointRowIndex] = useState<number | null>(null)
  const [{ page, hasMore, isLoading }, dispatchPagination] = usePagination()
  const internalScrollRef = useRef<HTMLDivElement>(null)
  const copySelectionRef = useRef<(() => void) | null>(null)
  const loadMore = () => {
    if (isLoading) return
    dispatchPagination({ type: 'NEXT_PAGE' })
  }

  const { rows, slots, rowByEntityId, rowIndexByEntityId } = useTimeGridLayout({
    entities,
    events,
    timeRange,
    granularity,
  })

  const scrollKeyboardEndpointIntoView = useUnsafeCallback(({ rowIndex }: { rowIndex: number }) => {
    setKeyboardEndpointRowIndex((prev) => {
      if (prev === rowIndex) {
        return prev
      }
      return rowIndex
    })
  }, [])

  const {
    selection,
    selectedCellKeys,
    selectedRowIds,
    selectedColumnIndexes,
    selectedEventKeys,
    clearSelection,
    handleCellSelected,
    handleRowHeaderSelected,
    handleColumnHeaderSelected,
    handleHeaderDragStart,
    handleCornerCellSelected,
    isAllSelected,
    handleEventSelected,
    fromCellKey,
    handleKeyboardRangeSelection,
  } = useGridSelection({
    rows,
    slots,
    rowIndexByEntityId,
    isCellSelectable,
    onKeyboardEndpointMoved: scrollKeyboardEndpointIntoView,
  })

  useEffect(() => {
    onSelectionChange?.(selection, slots)
  }, [selection])

  useGridClipboard({
    layout: { rows, slots, rowByEntityId, rowIndexByEntityId },
    selection: {
      selection,
      fromCellKey,
    },
    clipboardHandlers,
    clearSelection,
    onDelete: onDeleteShortcut,
    onCopyReady: (onCopy) => {
      copySelectionRef.current = onCopy
    },
  })

  useClearSelectionOnOutsidePress({
    rootRef: internalScrollRef,
    onClearSelection: clearSelection,
  })

  const handleAddShiftClick = useCallback(
    (entity: TEntity, slotIndex: number, anchorRect: DOMRect) => {
      onAddShiftClick?.(entity, slotIndex, anchorRect)
    },
    [onAddShiftClick]
  )

  const focusGrid = useUnsafeCallback(() => {
    internalScrollRef.current?.focus()
  }, [])

  const handleKeyDown = useUnsafeCallback(
    (event: KeyboardEvent) => {
      if (handleKeyboardRangeSelection({ key: event.key, shiftKey: event.shiftKey })) {
        event.preventDefault()
      }
    },
    [handleKeyboardRangeSelection]
  )

  const gridMinWidth = rowHeaderWidth + minColumnWidth * slots.length

  const content = (
    <Box
      tabIndex={0}
      role="grid"
      extra={{ onKeyDown: handleKeyDown }}
      style={{
        width: '100%',
        overflowX: enableHorizontalScroll ? 'auto' : 'hidden',
        overflowY: 'overlay',
      }}
      height="full-view-height"
      ref={internalScrollRef}
    >
      <TimeGridHeader
        rowHeaderLabel={rowHeaderLabel}
        rowHeaderWidth={rowHeaderWidth}
        minColumnWidth={minColumnWidth}
        gridMinWidth={gridMinWidth}
        showGridLines={showGridLines}
        stickyHeaders={stickyHeaders}
        granularity={granularity}
        slots={slots}
        timeSlotFormatter={timeSlotFormatter}
        selectedColumnIndexes={selectedColumnIndexes}
        isAllSelected={isAllSelected}
        onColumnHeaderSelected={(slotIndex, modifiers) => {
          focusGrid()
          handleColumnHeaderSelected(slotIndex, modifiers)
        }}
        onColumnHeaderDragStart={handleHeaderDragStart}
        onCornerCellSelected={handleCornerCellSelected}
      />
      <InfiniteScroll
        threshold={800}
        pageStart={page}
        loadMore={loadMore}
        hasMore={hasMore}
        useWindow={false}
        getScrollParent={() => internalScrollRef.current}
        style={{
          minWidth: 'min-content',
          flex: '1 0 auto',
          zIndex: 0,
          position: 'relative',
        }}
        loader={
          <Box alignItems="center" width="full" padding="s32" key="loading" flexGrow>
            <Spinner />
          </Box>
        }
      >
        <GridVirtualization
          rows={rows}
          rowMinHeight={rowMinHeight}
          scrollContainerRef={internalScrollRef}
          preloadedRowsOutsideViewport={preloadedRowsOutsideViewport}
          enableRowVirtualization={enableRowVirtualization}
          gridMinWidth={gridMinWidth}
          rowHeaderRenderer={rowHeaderRenderer}
          eventRenderer={eventRenderer}
          cardAppearance={cardAppearance}
          secondaryLayerRenderer={secondaryLayerRenderer}
          rowActionsRenderer={rowActionsRenderer}
          dragDropHandlers={dragDropHandlers}
          dragAndDropAdapter={dragAndDropAdapter}
          rowHeaderWidth={rowHeaderWidth}
          minColumnWidth={minColumnWidth}
          paddingX={paddingX}
          paddingY={paddingY}
          cardLayout={cardLayout}
          showGridLines={showGridLines}
          slots={slots}
          isCellSelectable={isCellSelectable}
          isCellDisabled={isCellDisabled}
          onCellSelected={handleCellSelected}
          selectedCellKeys={selectedCellKeys}
          selectedRowIds={selectedRowIds}
          selectedColumnIndexes={selectedColumnIndexes}
          selectedEventKeys={selectedEventKeys}
          isAllSelected={isAllSelected}
          onEventSelected={handleEventSelected}
          onCopySelection={() => copySelectionRef.current?.()}
          onRowHeaderSelected={(entity, modifiers) => {
            focusGrid()
            handleRowHeaderSelected(entity, modifiers)
          }}
          onHeaderDragStart={handleHeaderDragStart}
          onAddShiftClick={handleAddShiftClick}
          ghostCells={ghostCells}
          ghostAnchorCell={ghostAnchorCell}
          onGhostCardRef={onGhostCardRef}
          keyboardEndpointRowIndex={keyboardEndpointRowIndex}
          keyboardScrollMargin={keyboardScrollMargin}
        />
      </InfiniteScroll>
      {children}
    </Box>
  )

  return (
    <DndProvider options={HTML5toTouch} context={window}>
      {content}
    </DndProvider>
  )
}

export { TimeGridTable }
